import {
  MarketButton, MarketAccessory, MarketButtonDropdown, MarketList, MarketRow, MarketTooltip, MarketModalPartial,
  MarketHeader,
  MarketBanner,
} from '@market/react';
import type { TFunction } from 'i18next';
import {
  useRef, useState, type ReactElement, type ReactNode,
} from 'react';
import { Trans } from 'react-i18next';
import { MarketSkeletonLoader } from '@squareup/market-react';
import { MarketDropdown } from '@squareup/market-react/trial';
import { Icon, type IconName } from '@squareup/shared-ui-react';
import Gear from '../../../assets/svgs/Gear';
import { buildMarketBanner } from '../../../utils/build-market-banner';
import PencilWriting from '../../../assets/svgs/PencilWriting';
import QuestionMarkCircle from '../../../assets/svgs/QuestionMarkCircle';
import Trashcan from '../../../assets/svgs/Trashcan';
import { LocationSelector } from '../location-selector/LocationSelector';
import ArrowLeft from '../../../assets/svgs/ArrowLeft';
import CardSwipe from '../../../assets/svgs/CardSwipe';
import ChartBar from '../../../assets/svgs/ChartBar';
import RightLeft from '../../../assets/svgs/RightLeft';
import LoudSpeaker from '../../../assets/svgs/LoudSpeaker';
import Message from '../../../assets/svgs/Message';
import { formatCurrency } from '../../../utils';
import type { MoneyUnion } from '../../../utils/format-currency';
import { buildMarketList } from '../../../utils/build-market-list';
import styles from './ProductPageHeader.module.css';
import type {
  BalanceSubTextTooltip,
  BannerProps,
  HeaderButton,
  SettingsMenuRow,
  Subunit,
  HeaderDropdown,
  AccountProps,
} from './ProductPageHeader';

const settingsIconsMap: Partial<Record<IconName, ReactElement>> = {
  'pencil-writing': <PencilWriting />,
  'question-mark-circle': <QuestionMarkCircle />,
  trashcan: <Trashcan />,
  'card-swipe': <CardSwipe />,
  'chart-bar': <ChartBar />,
  'right-left': <RightLeft />,
  'loud-speaker': <LoudSpeaker />,
  message: <Message />,
};

export const GoBack = ({ t, goBack }: { t: TFunction; goBack: () => void }) => (
  <MarketButton
    icon-only
    className={styles.goBack}
    rank="secondary"
    size="medium"
    aria-label={t('bankingShared.goBack')}
    onClick={goBack}
  >
    <MarketAccessory slot="icon">
      <ArrowLeft />
    </MarketAccessory>
  </MarketButton>
);

export const Settings = ({
  t,
  goToSettings,
  settingsMenuRows,
  iconType = 'settings',
}: {
  t: TFunction;
  goToSettings?: () => void;
  settingsMenuRows?: SettingsMenuRow[];
  iconType?: 'settings' | 'help';
}) => {
  const isHelp = iconType === 'help';
  const ariaLabel = isHelp ? t('bankingShared.help') : t('bankingShared.settings');
  const IconComponent = isHelp ? QuestionMarkCircle : Gear;

  if (goToSettings) {
    return (
      <div className={styles.settings}>
        <MarketButton
          icon-only
          rank="secondary"
          size="medium"
          aria-label={ariaLabel}
          onClick={goToSettings}
        >
          <MarketAccessory slot="icon">
            <IconComponent />
          </MarketAccessory>
        </MarketButton>
      </div>
    );
  }

  if (settingsMenuRows?.length) {
    return (
      <div className={styles.settings}>
        <MarketDropdown placement="bottom-end">
          <MarketDropdown.Trigger>
            <MarketButton
              icon-only
              rank="secondary"
              size="medium"
              aria-label={ariaLabel}
            >
              <MarketAccessory slot="icon">
                <IconComponent />
              </MarketAccessory>
            </MarketButton>
          </MarketDropdown.Trigger>
          <MarketDropdown.Content>
            <MarketList interactive>
              {settingsMenuRows.map((row) => (
                <MarketRow
                  key={row.title}
                  transient
                  className={row.className}
                  onClick={row.onClick}
                >
                  {row.icon && (
                    <MarketAccessory size="icon" slot="leading-accessory">
                      {settingsIconsMap[row.icon]}
                    </MarketAccessory>
                  )}
                  <label slot="label">{row.title}</label>
                </MarketRow>
              ))}
            </MarketList>
          </MarketDropdown.Content>
        </MarketDropdown>
      </div>
    );
  }
};

export const AccountButton = ({ t, account }: { t: TFunction; account: AccountProps }) => {
  const [showAccountModal, setShowAccountModal] = useState(false);
  const { accountSuffix, accountNumber, routingNumber } = account || {};

  if (accountSuffix || accountNumber) {
    const last3 = String((accountSuffix || accountNumber || '')).slice(-3);
    return (
      <div className={styles.accountNumber}>
        <MarketButton
          rank="secondary"
          onClick={() => setShowAccountModal(true)}
        >
          {t('bankingShared.maskAccountNumber', { suffix: last3 })}
        </MarketButton>
        {showAccountModal && <AccountModal
          t={t}
          accountNumber={accountNumber}
          routingNumber={routingNumber}
          onClose={() => setShowAccountModal(false)} />}
      </div>
    );
  }
  return null;
};

const AccountModal = ({
  t, accountNumber, routingNumber, onClose,
}: { t: TFunction; accountNumber?: string; routingNumber?: string; onClose: () => void }) => {
  const [isAccountNumberCopied, setIsAccountNumberCopied] = useState(false);
  const [isRoutingNumberCopied, setIsRoutingNumberCopied] = useState(false);
  const handleCopy = async (text: string, setCopied: (bool: boolean) => void) => {
    await window.navigator?.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  // formatting the account and routing numbers in 3 + 3 + rest format
  const formatNumber = (number: string) => (String(number).replace(/^(\d{3})(\d{3})(\d*)$/, (_, g1, g2, rest) => [g1, g2, rest].filter(Boolean).join(' ')));

  return (
    <MarketModalPartial trapFocus onMarketDialogDismissed={onClose}>
      <MarketHeader><h2>{t('bankingShared.accountModal.title')}</h2></MarketHeader>
      <div>
        <MarketRow>
          <label slot="label">{t('bankingShared.accountModal.accountNumber')}</label>
          {accountNumber ?
            <>
              <p slot="subtext">{formatNumber(accountNumber)}</p>
              <MarketButton
                slot="trailing-accessory"
                rank="secondary"
                onClick={() => handleCopy(accountNumber ?? '', setIsAccountNumberCopied)}
              >
                <MarketAccessory slot="icon"><Icon iconName="copy" /></MarketAccessory>
                {isAccountNumberCopied ? t('bankingShared.accountModal.copied') : t('bankingShared.accountModal.copy')}
              </MarketButton>
            </>
            : <MarketSkeletonLoader size="medium" />}
        </MarketRow>
        <MarketRow>
          <label slot="label">{t('bankingShared.accountModal.routingNumber')}</label>
          {routingNumber ?
            <>
              <p slot="subtext">{formatNumber(routingNumber)}</p>
              <MarketButton
                slot="trailing-accessory"
                rank="secondary"
                onClick={() => handleCopy(routingNumber ?? '', setIsRoutingNumberCopied)}
              >
                <MarketAccessory slot="icon"><Icon iconName="copy" /></MarketAccessory>
                {isRoutingNumberCopied ? t('bankingShared.accountModal.copied') : t('bankingShared.accountModal.copy')}
              </MarketButton>
            </>
            : <MarketSkeletonLoader size="medium" />}
        </MarketRow>
        <MarketBanner>
          <span slot="title">{t('bankingShared.accountModal.guideTitle')}</span>
          {t('bankingShared.accountModal.guideDescription')}
          <a slot="action" href="https://squareup.com/help/us/en/article/7604-square-checking-transfers">
            {t('bankingShared.accountModal.learnMore')}
          </a>
        </MarketBanner>
      </div>
    </MarketModalPartial>
  );
};

export const ProductPageLocationSelector = ({
  currentUnitToken,
  units,
  handleSelectLocation,
  locationSelector,
}: {
  currentUnitToken?: string;
  units?: Subunit[];
  handleSelectLocation?: (location: Subunit) => void;
  locationSelector?: JSX.Element;
}) => {
  if (locationSelector) {
    return (
      <span
        className={styles.locationSelector}
        data-testid="product-page-header-location-selector"
      >
        {locationSelector}
      </span>);
  }

  if (units && units.length > 1) {
    return (
      <span
        className={styles.locationSelector}
        data-testid="product-page-header-location-selector"
      >
        <DefaultLocationSelector
          handleSelectLocation={handleSelectLocation}
          currentUnitToken={currentUnitToken}
          locations={units}
        />
      </span>);
  }

  return null;
};

const DefaultLocationSelector = ({
  handleSelectLocation,
  currentUnitToken,
  locations,
}: {
  handleSelectLocation?: (location: Subunit) => void;
  currentUnitToken?: string;
  locations: Subunit[];
}) => {
  const selectedLocation = locations.find((location) => location.token === currentUnitToken) ?? locations[0];
  const dropdownRef = useRef<HTMLMarketButtonDropdownElement>(null);
  const [searchInput, setSearchInput] = useState('');

  const searchLocation = () => {
    const normalizedQuery = searchInput.trim().toLowerCase();

    const filtered = normalizedQuery
      ? locations
        .filter((location) => location.nickname.toLowerCase().includes(normalizedQuery))
        .sort((a, b) => a.nickname.localeCompare(b.nickname))
      : locations;

    const result = filtered.map((location) => ({
      locationId: location.token,
      locationLabel: location.nickname,
      selectLocation: () => handleSelectLocation?.(location),
    }));

    return result;
  };

  return (
    <LocationSelector
      selectedLocationId={selectedLocation.token}
      selectedLocationLabel={selectedLocation.nickname}
      locations={searchLocation()}
      searchInputValue={searchInput}
      setSearchInputValue={setSearchInput}
      dropdownRef={dropdownRef}
      useCompactStyling={false}
    />
  );
};

export const Balance = ({
  t,
  balance,
  balanceSuperText,
  balanceSubText,
  balanceSubTextTooltip,
  shouldAddBottomSpacer,
}: {
  t: TFunction;
  balance: MoneyUnion;
  balanceSuperText?: string;
  balanceSubText?: string | ReactNode;
  balanceSubTextTooltip?: BalanceSubTextTooltip;
  shouldAddBottomSpacer?: boolean;
}) => (
  <>
    {(balanceSuperText || balance) && <div aria-hidden className={styles.spacerTop} />}
    {balanceSuperText && <p className={`paragraph-30 ${styles.balanceSuperText}`}>{balanceSuperText}</p>}
    <div className={styles.balance}>{formatCurrency({ currencyValue: balance })}</div>
    {balanceSubText && (
      <div className={`paragraph-30 ${styles.balanceSubText}`}>
        {balanceSubText}
      </div>
    )}
    {(balanceSubTextTooltip?.availableAmount || balanceSubTextTooltip?.pendingAmount) && (
      <div className={`paragraph-30 ${styles.balanceSubTextTooltip}`}>
        {balanceSubTextTooltip.availableAmount && (
          <div>{t('bankingShared.header.available', { availableAmount: formatCurrency({ currencyValue: balanceSubTextTooltip.availableAmount }) })}</div>
        )}
        {balanceSubTextTooltip.pendingAmount && (
          <div>{t('bankingShared.header.pending', { pendingAmount: formatCurrency({ currencyValue: balanceSubTextTooltip.pendingAmount }) })}</div>
        )}
        <MarketTooltip data-testid="product-page-header-tooltip">
          <span slot="content">
            <Trans i18nKey="bankingShared.header.tooltipV2" />
          </span>
        </MarketTooltip>
      </div>
    )}
    {shouldAddBottomSpacer && <div aria-hidden className={styles.spacerBottom} />}
  </>
);

export const Buttons = ({
  buttons,
  isMobile,
  shouldWrap,
}: {
  buttons: HeaderButton[];
  isMobile: boolean;
  shouldWrap: boolean;
}) => (
  <div className={`${styles.buttons} ${shouldWrap ? styles.buttonsWrapped : ''}`}>
    {buttons.map((button) => (
      <MarketButton
        key={button.copy}
        rank="primary"
        className={styles.buttonPrimary}
        disabled={button.isDisabled}
        is-loading={button.isLoading}
        data-testid="product-page-header-button"
        onClick={button.onClick}
      >
        {button.icon && !isMobile && (
          <MarketAccessory slot="icon">
            <Icon
              iconName={button.icon}
              fill={button.isDisabled ? 'var(--button-normal-variant-primary-rank-disabled-state-label-color)' : 'var(--core-fill-white-color)'}
            />
          </MarketAccessory>
        )}
        {button.copy}
      </MarketButton>
    ))
    }
  </div>
);

export const ButtonDropdown = ({ buttonDropdown }: { buttonDropdown: HeaderDropdown }) => (
  <div className={styles.buttons}>
    <MarketButtonDropdown
      data-testid="product-page-header-button-dropdown"
      disabled={buttonDropdown.isDisabled}
      onClick={buttonDropdown.onClick}
    >
      <MarketButton rank="primary" slot="trigger">{buttonDropdown.copy}</MarketButton>
      {buildMarketList(buttonDropdown.contentList)}
    </MarketButtonDropdown>
  </div>
);

export const Banners = ({ banners }: { banners: BannerProps[] }) => (
  <div className={styles.banners}>
    {banners.map((banner) => (
      buildMarketBanner(banner)
    ))}
  </div>
);

export const Error = ({ t, onTryAgain }: { t: TFunction; onTryAgain?: () => void }) => (
  <div className={styles.error} data-testid="product-page-header-error">
    <div className={`heading-20 ${styles.errorText}`}>{t('bankingShared.error.message')}</div>
    {onTryAgain && (
      <MarketButton data-testid="product-page-header-try-again" onClick={onTryAgain}>
        {t('bankingShared.error.tryAgain')}
      </MarketButton>
    )}
  </div>
);

export const Loading = () => (
  <div className={styles.loading} data-testid="product-page-header-loading">
    <MarketSkeletonLoader size="medium" />
    <MarketSkeletonLoader size="small" />
  </div>
);
